import { CountryInfo } from 'pages/insights-pro/country-tab/model';
import { MetricsChart } from 'components/MetricsChart';
import { DonutChartConfig } from 'components/chart/data/DonutChartConfig';
import { ChartDataArray } from 'components/chart/data/ChartDataArray';
import { ChartDataType } from 'components/chart/data/ChartDataType';
import { ChartData } from 'components/chart/data/ChartData';
import { ChartDataComparisonType } from 'components/chart/data/ChartDataComparisonType';
import { useIntl } from 'react-intl';
import { useMemo } from 'react';
import { DonutChartRenderer } from 'components/chart/renderer/DonutChartRenderer';
import { formatDateRangeLabel } from 'pages/insights-pro/country-tab/util';
import { getArrayWithIsOthers } from 'pages/insights-pro/common/graphs/getArrayWithIsOthers';
import { TileContent } from 'components/Layout/PageContainer';
import { Measure } from 'services/model/model.service';
import { useAppStore } from 'hooks/state/useAppStore';
import { getOthersColor } from 'components/chart/data/ChartColors';

interface CountrySharesProps {
  countries: CountryInfo[];
  isComparisonActive: boolean;
  gmvMeasure: Measure;
}

export function CountryShares({ countries, isComparisonActive, gmvMeasure }: CountrySharesProps) {
  const intl = useIntl();
  const { dateRange } = useAppStore();

  const donutData = useMemo(
    () =>
      getArrayWithIsOthers(countries, [gmvMeasure.code], intl, c => {
        return { ...c, color: getOthersColor() };
      }),
    [countries, isComparisonActive]
  );

  const createDonutChartConfig = () => {
    const donutChartConfig = new DonutChartConfig();
    donutChartConfig.value = {
      code: gmvMeasure.code,
      label: gmvMeasure.metric.name,
      unit: gmvMeasure.metric.unit,
    };
    donutChartConfig.legendConfig = {
      hide: false,
    };
    donutChartConfig.svgClassName = 'overflow-visible';

    donutChartConfig.data = [
      new ChartDataArray(
        ChartDataType.primary,

        formatDateRangeLabel(dateRange.range, intl),
        donutData.map(country => {
          const chartData = new ChartData(country.name, gmvMeasure.getValue(country.entry.values));
          chartData.color = country.color;
          return chartData;
        })
      ),
    ];
    if (isComparisonActive) {
      donutChartConfig.data.push(
        new ChartDataArray(
          ChartDataType.primary,
          formatDateRangeLabel(dateRange.comparison, intl),
          donutData.map(d => new ChartData(d.name, gmvMeasure.getValue(d.entry.compareValues))),
          ChartDataComparisonType.comparison
        )
      );
    }

    return donutChartConfig;
  };

  const countryShareRenderer = new DonutChartRenderer(createDonutChartConfig());

  return (
    <TileContent className="flex items-center justify-center">
      <MetricsChart renderer={countryShareRenderer} height="h-[300px]" />
    </TileContent>
  );
}
